import type { AiUsageSource } from "../../../shared/aiUsageSources";
import {
  estimateUsageCostUsd,
  type ModelPricing,
} from "../../../shared/aiModelPricing";
import type { MonitoringAiUsageRow } from "./aiUsage";
import { recordTokens } from "./aiUsage";

export const AI_USAGE_ACTIVITIES = ["build", "run", "monitor"] as const;
export type AiUsageActivity = (typeof AI_USAGE_ACTIVITIES)[number];

export const AI_USAGE_ACTIVITY_LABELS: Record<AiUsageActivity, string> = {
  build: "Build",
  run: "Run",
  monitor: "Monitor",
};

export function classifyAiUsageActivity(
  source: AiUsageSource | string
): AiUsageActivity | null {
  if (source === "workflow_run") return "run";
  if (source === "autofix" || source === "autotune") return "monitor";
  if (
    source === "workflow_builder" ||
    source === "branch_name" ||
    source === "commit_message" ||
    source === "context_recovery" ||
    source === "context_compaction" ||
    source === "summarize" ||
    source === "chat_name" ||
    source === "workflow_name" ||
    source === "chat_recap"
  ) {
    return "build";
  }
  return null;
}

export type ActivityCostBucket = {
  estimatedCostUsd: number | null;
  requestCount: number;
};

export function emptyActivityBuckets(): Record<
  AiUsageActivity,
  ActivityCostBucket
> {
  return {
    build: { estimatedCostUsd: null, requestCount: 0 },
    run: { estimatedCostUsd: null, requestCount: 0 },
    monitor: { estimatedCostUsd: null, requestCount: 0 },
  };
}

export function addRecordToActivityBuckets(
  buckets: Record<AiUsageActivity, ActivityCostBucket>,
  record: MonitoringAiUsageRow,
  pricing: ReadonlyMap<string, ModelPricing>
) {
  const activity = classifyAiUsageActivity(record.source);
  if (!activity) return;
  const cost = estimateUsageCostUsd(
    record.model,
    recordTokens(record),
    pricing,
    record.aiProviderId
  );
  const bucket = buckets[activity];
  bucket.requestCount += record.requestCount ?? 0;
  if (cost != null) {
    bucket.estimatedCostUsd = (bucket.estimatedCostUsd ?? 0) + cost;
  }
}
